import React, { useCallback, useEffect, useState } from 'react';
import type { FC } from 'react';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { isAdmin } from '@shared/helpers/user.helpers';
import { AI_MODEL_USE_CASES } from '@shared/models/ai/ai-model-use-cases.model';
import { IsolatedErrorBoundary } from '@client/ErrorBoundary';
import { resolveReportCoverImageRequest } from '@client/helpers/report-cover-image-status.helpers';
import { useIsConfirmed } from '@client/hooks/use-confirm.hook';
import { useErrorHandler } from '@client/hooks/use-error-handler.hooks';
import { useDebouncedSnackbar } from '@client/hooks/use-snackbar.hooks';
import { useUser } from '@client/hooks/use-user.hooks';
import { useUIState } from '@client/store/ui';
import {
  useBuildingDesign,
  useBuildingLocation,
} from '@features/design/hooks/design.hooks';
import {
  GenerateImageDialog,
  buildGenerateImageDialogDefaults,
  type GenerateImageDialogDefaults,
  type GenerateImageDialogValue,
} from '@features/design/components/GenerateImageDialog';
import { useReportCoverImage } from '../report-cover-image.context';

const COVER_MAP_NOT_READY_MESSAGE = 'The cover map is not ready yet';
const COVER_MODE = '3d' as const;
const REJECT_COVER_IMAGE_MESSAGE = {
  'reject-has-image': 'Remove the existing cover image first',
  'reject-no-image': 'No cover image to remove',
} as const;

/**
 * Consumes Admin Area create/remove cover-image requests.
 * Stays mounted in the report view so generate can use the cover map capture.
 */
export const ReportCoverImageController: FC = () => {
  const user = useUser();
  const confirm = useIsConfirmed();
  const buildingDesign = useBuildingDesign();
  const location = useBuildingLocation();
  const { status, generate, remove, isCaptureReady, getCamera } =
    useReportCoverImage();
  const errorHandler = useErrorHandler();
  const enqueueSnackbar = useDebouncedSnackbar();
  const { reportCoverImageRequest, setReportCoverImageRequest } = useUIState(
    'reportCoverImageRequest',
    'setReportCoverImageRequest',
  );
  const [dialogDefaults, setDialogDefaults] = useState<
    GenerateImageDialogDefaults | undefined
  >(undefined);

  /** Clear a consumed Admin Area request and any staged dialog defaults. */
  const clearRequest = useCallback(() => {
    setReportCoverImageRequest('none');
    setDialogDefaults(undefined);
  }, [setReportCoverImageRequest]);

  /** Open Generate-image once the cover map has a finite camera. */
  const openCreateDialog = useCallback(() => {
    const camera = getCamera();
    if (!camera) {
      enqueueSnackbar(COVER_MAP_NOT_READY_MESSAGE, { variant: 'warning' });
      clearRequest();
      return;
    }

    setDialogDefaults(
      buildGenerateImageDialogDefaults({
        camera,
        mode: COVER_MODE,
        location,
        buildingDesign,
        geminiModel: AI_MODEL_USE_CASES.reportCoverImageGemini.defaultModel,
      }),
    );
  }, [buildingDesign, clearRequest, enqueueSnackbar, getCamera, location]);

  /** Confirm then delete the stored cover image. */
  const confirmAndRemove = useCallback(async () => {
    const confirmed = await confirm({
      title: 'Remove cover image?',
      description:
        'This deletes the generated cover. You can create a new one afterwards.',
      cancellationText: 'Cancel',
      confirmationText: 'Remove',
    });
    if (!confirmed) return;
    await remove();
  }, [confirm, remove]);

  // Honour Admin Area requests once the cover map/status can satisfy them.
  useEffect(() => {
    if (reportCoverImageRequest === 'none') return;
    if (!isAdmin(user)) {
      clearRequest();
      return;
    }

    const resolution = resolveReportCoverImageRequest({
      request: reportCoverImageRequest,
      status,
      isCaptureReady,
    });

    switch (resolution) {
      case 'wait':
        return;
      case 'open-create':
        if (dialogDefaults) return;
        openCreateDialog();
        return;
      case 'confirm-remove':
        clearRequest();
        void confirmAndRemove();
        return;
      case 'reject-has-image':
      case 'reject-no-image':
        clearRequest();
        enqueueSnackbar(REJECT_COVER_IMAGE_MESSAGE[resolution], {
          variant: 'info',
        });
        return;
      default:
        assertUnreachable(resolution);
    }
  }, [
    clearRequest,
    confirmAndRemove,
    dialogDefaults,
    enqueueSnackbar,
    isCaptureReady,
    openCreateDialog,
    reportCoverImageRequest,
    status,
    user,
  ]);

  /**
   * Persist a generated cover, or just close the dialog on cancel.
   */
  const handleDialogClose = useCallback(
    async (value: GenerateImageDialogValue | undefined) => {
      const defaults = dialogDefaults;
      clearRequest();
      if (!value || !defaults) return;
      await generate(value, defaults.camera);
    },
    [clearRequest, dialogDefaults, generate],
  );

  /**
   * Close the dialog after an isolated render error so Admin Area stays usable.
   */
  const handleDialogError = useCallback(
    (error: Error) => {
      errorHandler(error);
      clearRequest();
    },
    [clearRequest, errorHandler],
  );

  const dialogOpen =
    reportCoverImageRequest === 'create' && Boolean(dialogDefaults);

  if (!dialogOpen || !dialogDefaults) return null;

  return (
    // Isolate dialog render errors so they do not reload the report into ErrorPage.
    <IsolatedErrorBoundary onError={handleDialogError} fallback={() => null}>
      <GenerateImageDialog
        open={dialogOpen}
        defaults={dialogDefaults}
        onClose={handleDialogClose}
        mode={COVER_MODE}
        location={location}
        buildingDesign={buildingDesign}
      />
    </IsolatedErrorBoundary>
  );
};
